/**
 * Which values a template needs, and what to call them.
 *
 * ★ ONE DEFINITION, SHARED WITH THE SERVER'S.
 * This mirrors `requiredKeys` in
 * `back/services/meta-whatsapp/campaign/variant.service.js`. The server decides
 * what it will accept; if this asks for a different set the agent either fills
 * a box that goes nowhere or is refused for a value the form never offered.
 * Lifted out of `campaigns/wizard/StepMessage.jsx`, which had its own copy —
 * and that copy still carried the header bug the server has since fixed.
 *
 * ★ A STATIC HEADER TAKES NOTHING. The wizard's copy asked for a header value
 * whenever `kind` was anything but 'none', including `{kind:'text', count:0}` —
 * a header of fixed copy with no placeholder. `hello_world`, the one approved
 * template in a fresh workspace, is exactly that shape, so the form demanded a
 * value Meta then rejected the message for.
 *
 * ★ ONLY HEADERS THE SERVER CAN BUILD COUNT. A media header takes a link, and
 * the inbox refuses those in this pass; a location header has no builder at
 * all. Neither may be asked for here.
 */

/** Header kinds the send path can actually assemble. */
const MEDIA_HEADER_KINDS = ['image', 'video', 'document'];

/** 0 or 1 — never more. Meta allows a single text-header parameter. */
export function headerParamCount(template) {
	const header = template?.params?.header ?? {};
	const kind = header.kind ?? 'none';
	if (kind === 'text') return Math.min(1, Number(header.count) || 0);
	return MEDIA_HEADER_KINDS.includes(kind) ? 1 : 0;
}

/**
 * The keys a template needs, in the order Meta expects them.
 *
 * Positional templates use '1','2',…; named ones use the names Meta returned.
 * `header` and `button:<n>` are separate keys because they are separate
 * components in the payload, not extra body parameters.
 */
export function requiredKeys(template) {
	if (!template) return [];
	const params = template.params ?? {};
	const keys = [];

	if (headerParamCount(template)) keys.push('header');

	const names = params.body?.names ?? [];
	if (names.length) names.forEach((name) => keys.push(name));
	else for (let i = 1; i <= (Number(params.body?.count) || 0); i += 1) keys.push(String(i));

	for (const index of params.buttons?.dynamicUrl ?? []) keys.push(`button:${index}`);
	return keys;
}

/** True when the inbox cannot send this template at all — see template.send.service.js. */
export function headerIsUnsupported(template) {
	const kind = template?.params?.header?.kind ?? 'none';
	return kind !== 'none' && kind !== 'text';
}

/**
 * A label for one key, for the form.
 *
 * A named template already tells us what the value is for; a positional one
 * only has a number, so the placeholder in the body is the closest thing to a
 * name and `describeKey` is deliberately plain rather than inventing one.
 */
export function describeKey(key) {
	if (key === 'header') return 'Header';
	if (key.startsWith('button:')) return `Button ${Number(key.slice(7)) + 1} link`;
	if (/^\d+$/.test(key)) return `Value ${key}`;
	// A named parameter: `first_name` → `First name`.
	return key
		.replace(/[_-]+/g, ' ')
		.trim()
		.replace(/^./, (c) => c.toUpperCase());
}

/**
 * What the server will refuse, checked here so it never has to.
 *
 * ★ THE SAME RULES, NOT SIMILAR ONES. `template.send.service.js` refuses an
 * empty value, a value over 1024 characters, and whitespace Meta rejects — and
 * refuses rather than repairing, because there is a preview on screen and a
 * silently shortened value would not be the message the agent approved. The
 * form says so before the send instead of after it.
 */
export const MAX_PARAM_LENGTH = 1024;
const ILLEGAL_WHITESPACE = /[\r\n\t]|\s{4,}/;

export function problemWith(value) {
	const text = String(value ?? '').trim();
	if (!text) return 'Required';
	if (ILLEGAL_WHITESPACE.test(text)) return 'No line breaks, tabs or long gaps';
	if (text.length > MAX_PARAM_LENGTH) return `Too long — ${text.length} of ${MAX_PARAM_LENGTH}`;
	return null;
}

/** Every key filled and valid. */
export function isComplete(template, values = {}) {
	const keys = requiredKeys(template);
	return keys.every((key) => !problemWith(values[key]));
}
